/* Signaux de passation THE SCAN (13/10/2026) : ce que fait le navigateur
 * pendant qu'un candidat est sur un ecran.
 *
 * POURQUOI. Le test du 13/10 : des IA qui pilotent un vrai navigateur passent
 * le test en 3 a 5 secondes par ecran. Le temps entre deux reponses est deja en
 * base ; ce qui manque, c'est le temps reellement passe SUR l'ecran, les sorties
 * de l'onglet, les collages et le comportement de la souris.
 *
 * CE QUE C'EST. Un resume par ecran et par passage, envoye au serveur. Jamais
 * un refus ni une note : ils nourrissent seulement l'indice du recruteur.
 *
 * CE QUE CE N'EST PAS. Aucune frappe n'est lue, aucun texte collé n'est
 * conserve (on compte seulement les collages), aucun deplacement de souris
 * n'est enregistre point par point : des compteurs.
 *
 * FINALITE de chaque compteur (minimisation des donnees) : `valeur` (temps
 * visible) et `horsOngletMs`/`sorties` nourrissent l'alerte de rythme et le
 * recruteur ; `collages` aussi ; `mouvements`, `clics`, `clicsMemePixel`,
 * `tactile` serviront a l'indice de vigilance (decision d'Hicham du 13/10) et ne
 * sont PAS encore montres au recruteur : a calibrer avec Nawal ; sans cet usage,
 * ils sont a retirer. Information des candidats et duree de conservation : a
 * trancher avant toute mise en ligne (A-REVOIR-AVANT-PROD-CLIENT.md).
 *
 * Le collecteur est pur (horloge injectee) : le branchement au navigateur est
 * dans `brancherNavigateur`, en bas. */

export const TYPE_ECRAN = 'ecran'
export const TYPE_VIDEO_PANNE = 'video_panne'
export const TYPE_VIDEO_VUE = 'video_vue'

/* Deux clics a moins de ce delai sont un double-clic : ils ne comptent pas
   comme « au meme endroit ». */
const DELAI_DOUBLE_CLIC_MS = 500

const resumeVide = (sequenceID, debut) => ({
  sequenceID,
  debut,
  horsOngletMs: 0,
  sorties: 0,
  collages: 0,
  mouvements: 0,
  clics: 0,
  clicsMemePixel: 0,
  tactile: false,
})

/* `envoyer(evenements)` est appele avec une liste d'evenements
   { type, sequenceID, valeur, detail }. `valeur` = millisecondes affichees.

   « Sortie » = l'onglet n'est plus visible (visibilitychange : autre onglet,
   fenetre reduite, ecran verrouille). Une simple perte de focus (barre d'adresse,
   notification, deuxieme ecran) n'en est PAS une et n'est pas comptee.

   Quand l'onglet se masque, le resume de l'ecran part tout de suite et l'ecran
   repart a zero : un onglet tue en arriere-plan (mobile) ne perd pas tout. Un
   meme ecran peut donc produire plusieurs resumes, qui s'additionnent. */
export const creerCollecteur = ({ maintenant = () => Date.now(), envoyer = () => {} } = {}) => {
  let ecran = null
  let dernierEcranID = null
  let masqueDepuis = null // instant ou l'onglet a cesse d'etre visible
  let dernierClic = null // { x, y, t } : au sein d'un meme ecran

  const hors = (t) => (masqueDepuis === null ? 0 : t - masqueDepuis)

  const resumer = (t) => {
    if (!ecran) return null
    const horsOngletMs = ecran.horsOngletMs + hors(t)
    const totalMs = Math.max(0, t - ecran.debut)
    return {
      type: TYPE_ECRAN,
      sequenceID: ecran.sequenceID,
      valeur: Math.max(0, totalMs - horsOngletMs), // temps visible sur l'ecran
      detail: {
        horsOngletMs,
        sorties: ecran.sorties,
        collages: ecran.collages,
        mouvements: ecran.mouvements,
        clics: ecran.clics,
        clicsMemePixel: ecran.clicsMemePixel,
        tactile: ecran.tactile,
      },
    }
  }

  const clore = () => {
    const r = resumer(maintenant())
    ecran = null
    dernierClic = null
    if (r) envoyer([r])
  }

  return {
    /* Etat de depart : une page chargee onglet masque ne compte pas ce temps comme visible. */
    initialiser(visible) { masqueDepuis = visible ? null : maintenant() },
    /* L'ecran change (ou le premier s'affiche) : on clot le precedent. */
    ecranAffiche(sequenceID) {
      dernierEcranID = sequenceID
      if (ecran && ecran.sequenceID === sequenceID) return
      clore()
      ecran = resumeVide(sequenceID, maintenant())
      if (masqueDepuis !== null) masqueDepuis = maintenant() // l'onglet cache compte pour le nouvel ecran
    },
    /* Retour depuis le cache du navigateur (pageshow persisted) : l'ecran etait clos au depart. */
    reprendre() {
      if (!ecran && dernierEcranID !== null) ecran = resumeVide(dernierEcranID, maintenant())
    },
    visibilite(visible) {
      const t = maintenant()
      if (!ecran) { masqueDepuis = visible ? null : t; return }
      if (!visible && masqueDepuis === null) {
        ecran.sorties += 1
        masqueDepuis = t
        // Le resume part maintenant, l'ecran repart a zero.
        const r = resumer(t)
        ecran = resumeVide(ecran.sequenceID, t)
        if (r) envoyer([r])
      } else if (visible && masqueDepuis !== null) {
        ecran.horsOngletMs += t - masqueDepuis
        masqueDepuis = null
      }
    },
    colle() { if (ecran) ecran.collages += 1 },
    mouvement() { if (ecran) ecran.mouvements += 1 },
    clic(x, y, typePointeur) {
      if (!ecran) return
      const t = maintenant()
      ecran.clics += 1
      if (typePointeur === 'touch' || typePointeur === 'pen') ecran.tactile = true
      if (dernierClic && dernierClic.x === x && dernierClic.y === y && t - dernierClic.t > DELAI_DOUBLE_CLIC_MS) ecran.clicsMemePixel += 1
      dernierClic = { x, y, t }
    },
    /* La video ne s'est pas lancee : un fait a part, pas un resume d'ecran. */
    videoPanne(sequenceID) {
      envoyer([{ type: TYPE_VIDEO_PANNE, sequenceID, valeur: null, detail: {} }])
    },
    /* Fermeture de l'onglet, fin de module : on envoie ce qui est en cours. */
    vider() { clore() },
  }
}

/* Branche un collecteur sur le navigateur. Rend la fonction qui debranche. */
export const brancherNavigateur = (collecteur, cible = window, doc = document) => {
  const surVisibilite = () => collecteur.visibilite(doc.visibilityState !== 'hidden')
  const surColler = () => collecteur.colle()
  const surMouvement = () => collecteur.mouvement()
  const surClic = (e) => collecteur.clic(e.screenX, e.screenY, e.pointerType)
  const surFermeture = () => collecteur.vider()
  const surRetour = (e) => { if (e && e.persisted) collecteur.reprendre() }
  collecteur.initialiser(doc.visibilityState !== 'hidden')
  doc.addEventListener('visibilitychange', surVisibilite)
  doc.addEventListener('paste', surColler, true)
  doc.addEventListener('pointermove', surMouvement, { passive: true })
  doc.addEventListener('pointerdown', surClic, true)
  cible.addEventListener('pagehide', surFermeture)
  cible.addEventListener('pageshow', surRetour)
  return () => {
    doc.removeEventListener('visibilitychange', surVisibilite)
    doc.removeEventListener('paste', surColler, true)
    doc.removeEventListener('pointermove', surMouvement, { passive: true })
    doc.removeEventListener('pointerdown', surClic, true)
    cible.removeEventListener('pagehide', surFermeture)
    cible.removeEventListener('pageshow', surRetour)
  }
}

/* Le collecteur du lecteur en cours (un seul), pour que FormatVideo puisse
   signaler une panne sans que Formation lui passe l'objet. */
const actif = { collecteur: null }
export const definirCollecteurActif = (c) => { actif.collecteur = c }
export const signalerVideoPanne = (sequenceID) => { if (actif.collecteur) actif.collecteur.videoPanne(sequenceID) }
